import React, { useCallback, useContext, useEffect, useState } from "react";
import "./scenario-tab-content.component.scoped.scss";
import { Box, Button, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { services } from "../../../../../services";
import { CaseContext } from "../../../../../store";
import { Skeleton } from "@material-ui/lab";
import { showPopupToastMsg, showToastMsg } from "../../../../../helpers";
import { ResponsablePersonComponent } from "../../../../../components/responsable-person/responsable-person.component";
import TabContentTitleComponent from "../tab-content-title/tab-content-title.component";
import { EntityTypes } from "../../../types/entity-select";
import EntitySelectDialog from "../../../dialogs/entity-select/entity-select.dialog";

const ScenarioTabContentComponent = () => {
  const { t } = useTranslation();
  const {
    caseDetails,
    caseDetailsForm,
    setCaseDetailsForm,
    setCaseDetails,
    loading,
    ignoreUpdateCase,
  } = useContext(CaseContext);

  const [editMode, setEditMode] = useState(false);
  const [showProductSelectDialog, setShowProductSelectDialog] = useState(false);
  const [showRoleSelectDialog, setShowRoleSelectDialog] = useState(false);
  const [showRiskLevelSelectDialog, setShowRiskLevelSelectDialog] = useState(
    false,
  );

  useEffect(() => {
    setEditMode(false);
  }, [caseDetails, ignoreUpdateCase]);

  const handleProductSelected = useCallback(
    async (product: any) => {
      const res = await services.cases.update(caseDetails.id, {
        productId: product.id,
        version: ++caseDetailsForm.version,
      });
      if (res?.message === "SUCCESS") {
        setCaseDetails({
          ...caseDetails,
          productId: product.id,
          product: {
            ...caseDetails.product,
            id: product.id,
            name: product.name,
            version: product.version ?? caseDetails.product?.version,
            version_details:
              product.version_details ?? caseDetails.product?.version_details,
          },
        });
        showToastMsg("success", t("CASE_UPDATED_SUCCESSFULLY"));
      } else {
        showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
      }
    },
    [t, caseDetails, setCaseDetails, caseDetailsForm],
  );

  const handleRoleSelected = useCallback(
    async (role: any) => {
      const res = await services.cases.update(caseDetails.id, {
        roleId: role.id,
        riskLevelId: caseDetails.riskLevelId,
        version: ++caseDetailsForm.version,
      });
      if (res?.message === "SUCCESS") {
        setCaseDetails({
          ...caseDetails,
          roleId: role.id,
          role: {
            ...caseDetails.role,
            id: role.id,
            name: role.name,
            slug: role.slug,
          },
        });
        showToastMsg("success", t("CASE_UPDATED_SUCCESSFULLY"));
      } else {
        showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
      }
    },
    [t, caseDetails, setCaseDetails, caseDetailsForm],
  );

  const handleRiskLevelSelected = useCallback(
    async (riskLevel: any) => {
      const res = await services.cases.update(caseDetails.id, {
        riskLevelId: riskLevel.id,
        roleId: caseDetails.roleId,
        version: ++caseDetailsForm.version,
      });
      if (res?.message === "SUCCESS") {
        setCaseDetails({
          ...caseDetails,
          riskLevelId: riskLevel.id,
          riskLevel: {
            ...caseDetails.riskLevel,
            id: riskLevel.id,
            name: riskLevel.name,
            slug: riskLevel.slug,
            iconKey:
              riskLevel.iconKey ??
              riskLevel.icon_key ??
              caseDetails.riskLevel?.iconKey,
          },
        });
        showToastMsg("success", t("CASE_UPDATED_SUCCESSFULLY"));
      } else {
        showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
      }
    },
    [t, caseDetails, setCaseDetails, caseDetailsForm],
  );

  return (
    <Box className="scenario__content">
      <TabContentTitleComponent
        title={t("USECASE_DETAILS.USER_CASE_DETAILS_INFORMATION")}
        subTitle={t(
          "USECASE_DETAILS.ORGANIZE_THE_CORE_INFORMATION_OF_YOUR_USE_CASE",
        )}
        loading={loading}
      />
      {/* Details */}
      <Box className="scenario__content__details">
        <Box
          className="global__box__content p-0"
          style={{ height: "fit-content" }}
        >
          <Box className="global__box__content__header d-flex justify-content-between align-items-center">
            {loading ? (
              <Skeleton variant="text" height={20} width={300} />
            ) : (
              <Typography
                className="text-capitalize"
                variant="h3"
                color="textPrimary"
              >
                {t(
                  "USECASE_DETAILS.MANAGE_USE_CASE_TITLE_SUMMARY_AND_DESCRIPTION",
                )}
              </Typography>
            )}

            {loading ? (
              <Skeleton
                variant="rect"
                width={100}
                height={30}
                style={{ borderRadius: "4px" }}
              />
            ) : (
              <>
                {editMode ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    style={{ minHeight: "40px" }}
                    className="btn-primary-theme"
                    size="small"
                    onClick={() => setEditMode(false)}
                  >
                    {t("CANCEL")}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ minHeight: "40px" }}
                    className="btn-primary-theme"
                    size="small"
                    onClick={() => setEditMode(true)}
                  >
                    {t("EDIT")}
                  </Button>
                )}
              </>
            )}
          </Box>
          <Box className="global__box__content__body">
            {editMode ? (
              <Box className="form">
                <Box className="form__item">
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className="text-capitalize"
                    style={{ fontSize: "13px" }}
                  >
                    {t("USECASE_DETAILS.TITLE")}
                  </Typography>
                  <Box className="standard_text_input">
                    <TextField
                      className="w-100"
                      placeholder={t("USECASE_DETAILS.TITLE")}
                      onChange={(e: any) =>
                        setCaseDetailsForm({
                          ...caseDetailsForm,
                          title: e.target.value.replace(/^\s+/g, ""),
                        })
                      }
                      value={caseDetailsForm.title}
                      name="name"
                      variant="standard"
                      inputProps={{
                        maxLength: 150,
                      }}
                    />
                  </Box>
                </Box>
                <Box className="form__item">
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className="text-capitalize"
                    style={{ fontSize: "13px" }}
                  >
                    {t("USECASE_DETAILS.SUMMARY")}
                  </Typography>
                  <Box className="standard_text_input">
                    <TextField
                      className="w-100 text-area-multiline"
                      placeholder={t("USECASE_DETAILS.SUMMARY")}
                      onChange={(e: any) =>
                        setCaseDetailsForm({
                          ...caseDetailsForm,
                          brief: e.target.value.replace(/^\s+/g, ""),
                        })
                      }
                      value={caseDetailsForm.brief}
                      name="brief"
                      variant="standard"
                      multiline
                      rows={7}
                      inputProps={{
                        maxLength: 500,
                      }}
                    />
                  </Box>
                </Box>
                <Box className="form__item">
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className="text-capitalize"
                    style={{ fontSize: "13px" }}
                  >
                    {t("USECASE_DETAILS.DESCRIPTION")}
                  </Typography>
                  <Box className="standard_text_input">
                    <TextField
                      className="w-100 text-area-multiline"
                      placeholder={t("USECASE_DETAILS.DESCRIPTION")}
                      onChange={(e: any) =>
                        setCaseDetailsForm({
                          ...caseDetailsForm,
                          description: e.target.value.replace(/^\s+/g, ""),
                        })
                      }
                      value={caseDetailsForm.description}
                      name="description"
                      variant="standard"
                      multiline
                      rows={10}
                      inputProps={{
                        maxLength: 2500,
                      }}
                    />
                  </Box>
                </Box>
                <Box className="form__item">
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className="text-capitalize"
                    style={{ fontSize: "13px" }}
                  >
                    {t("USECASE_DETAILS.SCENARIO_DESCRIPTION")}
                  </Typography>
                  <Box className="standard_text_input">
                    <TextField
                      className="w-100 text-area-multiline"
                      placeholder={t("USECASE_DETAILS.SCENARIO_DESCRIPTION")}
                      onChange={(e: any) =>
                        setCaseDetailsForm({
                          ...caseDetailsForm,
                          scenarioDescription: e.target.value.replace(
                            /^\s+/g,
                            "",
                          ),
                        })
                      }
                      value={caseDetailsForm.scenarioDescription}
                      name="scenarioDescription"
                      variant="standard"
                      multiline
                      rows={10}
                      inputProps={{
                        maxLength: 2500,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box className="items_view">
                <Box className="item">
                  {loading ? (
                    <Skeleton
                      variant="text"
                      height={15}
                      width={100}
                      className="mb-1"
                    />
                  ) : (
                    <Typography
                      variant="h5"
                      style={{ color: "#979797" }}
                      className="text-capitalize"
                    >
                      {t("USECASE_DETAILS.TITLE")}
                    </Typography>
                  )}
                  {loading ? (
                    <Skeleton variant="text" height={22} width="90%" />
                  ) : (
                    <Typography
                      variant="h1"
                      color="textPrimary"
                      className="text-capitalize"
                    >
                      {caseDetails.title}
                    </Typography>
                  )}
                </Box>
                <Box className="item">
                  {loading ? (
                    <Skeleton
                      variant="text"
                      height={15}
                      width={100}
                      className="mb-1"
                    />
                  ) : (
                    <Typography
                      variant="h5"
                      style={{ color: "#979797" }}
                      className="text-capitalize"
                    >
                      {t("USECASE_DETAILS.SUMMARY")}
                    </Typography>
                  )}
                  {loading ? (
                    <>
                      <Skeleton variant="text" height={20} width="80%" />
                      <Skeleton variant="text" height={20} width="60%" />
                      <Skeleton variant="text" height={20} width="70%" />
                    </>
                  ) : (
                    <Typography variant="body1" color="textPrimary">
                      {caseDetails.brief}
                    </Typography>
                  )}
                </Box>
                <Box className="item">
                  {loading ? (
                    <Skeleton
                      variant="text"
                      height={15}
                      width={100}
                      className="mb-1"
                    />
                  ) : (
                    <Typography
                      variant="h5"
                      style={{ color: "#979797" }}
                      className="text-capitalize"
                    >
                      {t("USECASE_DETAILS.DESCRIPTION")}
                    </Typography>
                  )}
                  {loading ? (
                    <>
                      <Skeleton variant="text" height={20} width="80%" />
                      <Skeleton variant="text" height={20} width="60%" />
                      <Skeleton variant="text" height={20} width="70%" />
                      <Skeleton variant="text" height={20} width="50%" />
                      <Skeleton variant="text" height={20} width="75%" />
                      <Skeleton variant="text" height={20} width="60%" />
                    </>
                  ) : (
                    <Typography variant="body1" color="textPrimary">
                      {caseDetails.description}
                    </Typography>
                  )}
                </Box>
                {caseDetails.scenarioDescription && (
                  <Box className="item">
                    {loading ? (
                      <Skeleton
                        variant="text"
                        height={15}
                        width={100}
                        className="mb-1"
                      />
                    ) : (
                      <Typography
                        variant="h5"
                        style={{ color: "#979797" }}
                        className="text-capitalize"
                      >
                        {t("USECASE_DETAILS.SCENARIO_DESCRIPTION")}
                      </Typography>
                    )}
                    {loading ? (
                      <>
                        <Skeleton variant="text" height={20} width="80%" />
                        <Skeleton variant="text" height={20} width="60%" />
                        <Skeleton variant="text" height={20} width="70%" />
                        <Skeleton variant="text" height={20} width="50%" />
                        <Skeleton variant="text" height={20} width="75%" />
                        <Skeleton variant="text" height={20} width="60%" />
                      </>
                    ) : (
                      <Typography variant="body1" color="textPrimary">
                        {caseDetails.scenarioDescription}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box className="scenario__content__info">
          <ResponsablePersonComponent
            user={caseDetails.keyContactUser}
            loading={loading}
            editable={true}
            title={t("KEY_CONTACT_USER")}
          />

          {/* Risk Level */}
          {loading ? (
            <Skeleton variant="text" height={90} width="100%" />
          ) : (
            <Box className="risk__level__content global__box__content d-flex align-items-center justify-content-between">
              <Box className="d-flex flex-column">
                <Typography
                  variant="subtitle1"
                  className="text-capitalize font-weight-medium"
                >
                  {caseDetails?.riskLevel?.name}
                </Typography>
                <Typography variant="h5">{t("RISK_LEVEL")}</Typography>
              </Box>
              <Typography
                variant="body1"
                className="font-weight-medium"
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => setShowRiskLevelSelectDialog(true)}
              >
                {t("EDIT")}
              </Typography>
            </Box>
          )}
          {showRiskLevelSelectDialog && (
            <EntitySelectDialog
              show={showRiskLevelSelectDialog}
              onClose={() => setShowRiskLevelSelectDialog(false)}
              entity={EntityTypes.RISK_LEVEL}
              currentEntityId={
                caseDetails.riskLevelId ?? caseDetails.riskLevel?.id
              }
              onSelect={handleRiskLevelSelected}
            />
          )}

          {/* Product */}
          {loading ? (
            <Skeleton variant="text" height={90} width="100%" />
          ) : (
            <Box className="global__box__content d-flex align-items-center justify-content-between">
              <Box className="d-flex flex-column">
                <Typography
                  variant="subtitle1"
                  className="text-capitalize font-weight-medium"
                  style={{
                    textDecoration: "underline",
                    color: "var(--accent-color)",
                  }}
                >
                  {caseDetails?.product?.name}
                </Typography>
                <Typography variant="h5">{t("PRODUCT")}</Typography>
              </Box>
              <Typography
                variant="body1"
                className="font-weight-medium"
                style={{
                  color: "var(--accent-color)",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => setShowProductSelectDialog(true)}
              >
                {t("EDIT")}
              </Typography>
            </Box>
          )}
          {showProductSelectDialog && (
            <EntitySelectDialog
              show={showProductSelectDialog}
              onClose={() => setShowProductSelectDialog(false)}
              entity={EntityTypes.PRODUCT}
              currentEntityId={caseDetails.productId ?? caseDetails.product?.id}
              onSelect={handleProductSelected}
            />
          )}
          {/* Product Version */}
          {loading ? (
            <Skeleton variant="text" height={90} width="100%" />
          ) : (
            <Box className="global__box__content d-flex align-items-center justify-content-between">
              <Box className="d-flex flex-column">
                <Typography
                  variant="subtitle1"
                  className="text-capitalize font-weight-medium"
                >
                  {caseDetails?.product?.version}
                </Typography>
                <Typography variant="h5">{t("PRODUCT_VERSION")}</Typography>
              </Box>
            </Box>
          )}
          {/* Role */}
          {loading ? (
            <Skeleton variant="text" height={90} width="100%" />
          ) : (
            <Box className="global__box__content d-flex align-items-center justify-content-between">
              <Box className="d-flex flex-column">
                <Typography
                  variant="subtitle1"
                  className="text-capitalize font-weight-medium"
                >
                  {caseDetails?.role?.name}
                </Typography>
                <Typography variant="h5">{t("ROLE")}</Typography>
              </Box>
              <Typography
                variant="body1"
                className="font-weight-medium"
                style={{
                  color: "var(--accent-color)",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => setShowRoleSelectDialog(true)}
              >
                {t("EDIT")}
              </Typography>
            </Box>
          )}
          {showRoleSelectDialog && (
            <EntitySelectDialog
              show={showRoleSelectDialog}
              onClose={() => setShowRoleSelectDialog(false)}
              entity={EntityTypes.ROLE}
              currentEntityId={caseDetails.roleId ?? caseDetails.role?.id}
              onSelect={handleRoleSelected}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ScenarioTabContentComponent;
